import { PAGE_PATH } from '@/constants';
import {
  fetchActiveChannel,
  fetchGetDistributor,
  fetchRegionsJson,
  fetchSettings,
} from '@/services';
import {
  fetchActiveOrderByShoppingTrolley,
  fetchGetSubscribeMessageTemplateId,
  fetchShareSettings,
} from '@/services/apis';
import { SettingKey, ShareSettingPageKey, type AnyObj, type AnyObjOrNull } from '@/types';
import type { MpApp } from '@/types/global-business';
import { defineStore } from 'pinia';

interface State {
  /** matomo是否已完成初始化 */
  isMatomoReady: boolean;
  /** 全局loading标识 */
  appLoadingProviderFlag: boolean;
  /** 全局订阅开关 */
  subscribeGlobalSwitch: boolean;
  /** 全局渠道配置信息 */
  channelConfig: AnyObjOrNull;
  /** 省市区地址数据 */
  regionsJson: any[];
  /** 进入会员页之前的tabbar路由数据缓存 */
  beforeRouteToMemberTabbarCache: MpApp.BeforeRouteToMemberTabbarCacheType;
  /** 富文本页面携带数据 */
  richTextData: {
    pageTitle: string;
    data: AnyObjOrNull;
  };
  /** 是否渲染page-meta样式，目前主要用于禁止滚动穿透 */
  displayPageMeta: boolean;
  /** 分销员数据 */
  distributorData: AnyObjOrNull;
  /** 分享分销数据解析 */
  shareDistributorData: MpApp.shareDistributor | null;
  /** 精选商品配置 */
  settingsConfig: AnyObj[];
  /** 通知支付未完成标识 */
  noticcNotPaymentCompleteFlag: boolean;
  /** 购物车角标数量 */
  shoppingTrolleyCount: number;
  /** 资源预览器选项 */
  resourcePreviewerOptions: MpApp.ResourcePreviewOptions;
  /** 全局toast提示选项 */
  toastOptions: MpApp.ToastOptions;
  /** 模版id */
  subscribeMessageTemplateIds: AnyObjOrNull;
  /** 网络慢、异常标识 */
  networkErrorFlag: boolean;
  /** 测试授权元素 */
  testAuthElement: boolean;
  /** 通用异常提示弹窗 */
  abnormalPromptPopupConfig: {
    ref: AnyObject | null;
    message: string;
    maskClick?: boolean;
    callback?: null | (() => void);
  };
  /** 分享设置页面key */
  shareSettingPageKey: Map<ShareSettingPageKey, AnyObject>;
}

export const useAppStore = defineStore('app', {
  state: (): State => ({
    isMatomoReady: false,
    appLoadingProviderFlag: true,
    subscribeGlobalSwitch: false,
    channelConfig: null,
    regionsJson: [],
    beforeRouteToMemberTabbarCache: {
      index: 0,
      pagePath: PAGE_PATH.home.route,
      text: '首页',
      isback: false,
    },
    richTextData: {
      pageTitle: '',
      data: null,
    },
    displayPageMeta: false,
    distributorData: null,
    shareDistributorData: null,
    settingsConfig: [],
    noticcNotPaymentCompleteFlag: false,
    shoppingTrolleyCount: -1,
    resourcePreviewerOptions: {
      display: false,
      data: [],
      defaultIndex: 0,
    },
    toastOptions: {
      visible: false,
      message: 'string',
      isLoading: false,
    },
    subscribeMessageTemplateIds: null,
    networkErrorFlag: false,
    testAuthElement: false,
    abnormalPromptPopupConfig: {
      ref: null,
      message: '',
      maskClick: false,
      callback: null,
    },
    shareSettingPageKey: new Map(),
  }),

  getters: {},

  actions: {
    /**
     * 获取全局渠道配置信息
     */
    async getShopConfigs(refresh?: boolean) {
      if (!this.channelConfig || refresh) {
        const res = await fetchActiveChannel();
        if (res) this.channelConfig = res;
      }
      return this.channelConfig;
    },
    /** 获取分销员数据 */
    async getGetDistributor(refresh?: boolean) {
      if (!this.distributorData || refresh) {
        const data = await fetchGetDistributor();
        if (data) this.distributorData = data;
      }
      return this.distributorData;
    },
    /** 获取精选商品配置 */
    async getSettings(refresh?: boolean) {
      if (!this.settingsConfig.length || refresh) {
        const data = await fetchSettings([
          SettingKey.memberCardProductGroup,
          SettingKey.personalCenterProductGroup,
          SettingKey.searchResultProductGroup,
          SettingKey.shoppingCartProductGroup,
          SettingKey.couponPageProductGroup,
          SettingKey.productDetailProductGroup,
          SettingKey.canBackProductDetailPage,
          SettingKey.forumRecommendProductGroup,
        ]);
        if (data) this.settingsConfig = data;
      }
      return this.settingsConfig;
    },
    /** 获取分享页面设置 */
    async getShareSettingPageKey(pageKeys: ShareSettingPageKey[] = []) {
      const _keys = [
        ShareSettingPageKey.forumPost,
        ShareSettingPageKey.pointsMall,
        ShareSettingPageKey.pointsCheckin,
        ...pageKeys,
      ];
      if (!this.shareSettingPageKey.size) {
        const data = await fetchShareSettings(_keys);
        if (data)
          this.shareSettingPageKey = new Map(data.map((item: AnyObject) => [item.pageKey, item]));
      }
      console.log(`获取分享页面设置：`, this.shareSettingPageKey);

      return this.shareSettingPageKey;
    },
    /** 获取省市区数据 */
    async getRegionsJson(refresh?: boolean) {
      if (!this.regionsJson.length || refresh) {
        this.regionsJson = (await fetchRegionsJson()) as AnyObj[];
      }
      return this.regionsJson;
    },
    /** 获取购物车角标数量 */
    async getShoppingTrolleyCount() {
      const data = await fetchActiveOrderByShoppingTrolley();
      if (data) {
        this.shoppingTrolleyCount = data.lines.length;
      }
    },
    /** 获取模版id集合 */
    async getSubscribeMessageTemplateId() {
      const data = await fetchGetSubscribeMessageTemplateId();
      if (data) {
        this.subscribeMessageTemplateIds = data;
      }
    },
  },
});
